import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Calendar, MapPin, ChevronLeft, ChevronRight, Ticket, Clock, Sparkles, Share2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { eventsApi } from '../lib/api';
import { formatEventDate, parseEventDate } from '../lib/dateUtils';
import EventPromoBadge from '../components/EventPromoBadge';
import { toast } from 'sonner';

// ============================================
// HERO SLIDER - Con preview del próximo evento
// ============================================
const HeroSlider = ({ events }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => {
    if (!isAutoPlaying || events.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % events.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, events.length]);

  const goToPrev = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + events.length) % events.length);
  }, [events.length]);

  const goToNext = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % events.length);
  }, [events.length]);

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? goToNext() : goToPrev();
    }
    setTouchStart(null);
  };

  if (!events.length) return null;

  const currentEvent = events[currentIndex];
  const nextEvent = events[(currentIndex + 1) % events.length];
  const eventDate = parseEventDate(currentEvent.date);

  return (
    <div 
      className="relative w-full"
      data-testid="hero-slider"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Container principal */}
      <div className="relative aspect-[3/4] sm:aspect-[16/10] lg:aspect-[21/9] overflow-hidden bg-black">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0"
          >
            <img
              src={currentEvent.image_url || 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920'}
              alt={currentEvent.title}
              className={`w-full h-full object-cover ${
                currentEvent.image_position === 'top' ? 'object-top' : 
                currentEvent.image_position === 'bottom' ? 'object-bottom' : 
                'object-center'
              }`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/70 via-transparent to-transparent" />
          </motion.div>
        </AnimatePresence>

        {/* Content */}
        <div className="absolute inset-0 flex items-end pb-6 sm:pb-0 sm:items-center">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:pr-72">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="max-w-xl"
              >
                {/* Productora info - Siempre visible y prominente */}
                <Link
                  to={currentEvent.producer_id ? `/productora/${currentEvent.producer_slug || currentEvent.producer_id}` : '#'}
                  className="inline-flex items-center gap-2.5 px-3 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 hover:border-[#D4AF37]/50 hover:bg-white/15 transition-all mb-3 group"
                  data-testid="hero-producer-badge"
                >
                  {currentEvent.producer_logo ? (
                    <img 
                      src={currentEvent.producer_logo} 
                      alt={currentEvent.producer_name || 'Productora'} 
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border border-white/30"
                    />
                  ) : (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#D4AF37]/30 border border-[#D4AF37]/50 flex items-center justify-center">
                      <span className="text-xs sm:text-sm font-bold text-[#D4AF37]">
                        {currentEvent.producer_name?.charAt(0) || 'P'}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] sm:text-xs text-white/50 uppercase tracking-wider font-semibold">Presenta</span>
                    <span className="text-sm sm:text-base text-white font-bold group-hover:text-[#D4AF37] transition-colors">
                      {currentEvent.producer_name || 'Productora'}
                    </span>
                  </div>
                </Link>

                {/* Fecha en línea separada */}
                <div className="mb-4 flex items-center gap-2 flex-wrap">
                  <div className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-3 py-1.5 rounded-sm">
                    <Calendar size={14} />
                    <span className="font-mono font-bold text-xs uppercase tracking-wider">
                      {formatEventDate(currentEvent.date, "EEE d MMM")}
                    </span>
                  </div>
                  <EventPromoBadge promo={currentEvent.active_promo} />
                </div>

                <Link to={`/evento/${currentEvent.slug || currentEvent.id}`}>
                  <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white mb-2 hover:text-[#D4AF37] transition-colors leading-none">
                    {currentEvent.title}
                  </h2>
                </Link>

                <div className="flex flex-wrap items-center gap-3 text-white/70 text-sm mb-4">
                  {currentEvent.time && (
                    <div className="flex items-center gap-1.5">
                      <Clock size={14} className="text-[#D4AF37]" />
                      <span className="font-mono">{currentEvent.time}hs</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <MapPin size={14} className="text-[#D4AF37]" />
                    <span className="truncate max-w-[150px] sm:max-w-[250px]">{currentEvent.venue_name || currentEvent.location}</span>
                  </div>
                </div>

                <Link
                  to={`/evento/${currentEvent.slug || currentEvent.id}`}
                  data-testid={`hero-cta-${currentEvent.id}`}
                  className="inline-flex items-center gap-2 bg-white text-black px-4 py-2.5 sm:px-6 sm:py-3 font-bold uppercase tracking-wider text-xs sm:text-sm hover:bg-[#D4AF37] transition-all duration-300"
                >
                  <Ticket size={16} />
                  <span>Comprar</span>
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Navigation Arrows - Solo en tablet (desktop usa preview clickeable) */}
        {events.length > 1 && (
          <>
            {/* Flechas solo para tablet (sin preview) */}
            <button
              onClick={goToPrev}
              data-testid="hero-prev-btn-tablet"
              className="hidden sm:flex lg:hidden absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/60 hover:bg-black/90 border border-white/20 hover:border-[#D4AF37] items-center justify-center text-white hover:text-[#D4AF37] transition-all duration-300 z-20"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={goToNext}
              data-testid="hero-next-btn-tablet"
              className="hidden sm:flex lg:hidden absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-black/60 hover:bg-black/90 border border-white/20 hover:border-[#D4AF37] items-center justify-center text-white hover:text-[#D4AF37] transition-all duration-300 z-20"
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}

        {/* Preview del siguiente evento - Solo Desktop */}
        {events.length > 1 && (
          <div className="hidden lg:block absolute right-6 top-1/2 -translate-y-1/2 w-48 z-10">
            <div 
              className="relative aspect-[3/4] overflow-hidden border-2 border-white/20 hover:border-[#D4AF37] cursor-pointer transition-all duration-300 group bg-black"
              onClick={goToNext}
            >
              <img
                src={nextEvent.image_url || 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=400'}
                alt={nextEvent.title}
                className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <span className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider">Siguiente</span>
                <p className="text-sm font-bold text-white line-clamp-2 mt-1 leading-tight">
                  {nextEvent.title}
                </p>
                <p className="text-xs text-white/60 font-mono mt-1">
                  {formatEventDate(nextEvent.date, "d MMM")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Indicators */}
        {events.length > 1 && (
          <div className="absolute bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
            {events.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAutoPlaying(false);
                  setCurrentIndex(index);
                }}
                data-testid={`hero-indicator-${index}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-[#D4AF37] w-6' 
                    : 'bg-white/40 hover:bg-white/60 w-1.5'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// SLOGAN SECTION - E.Ú.G.E Prominente
// ============================================
const SloganSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="relative py-12 md:py-20 bg-[#0A0A0A] overflow-hidden"
      data-testid="slogan-section"
    >
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[500px] h-[250px] bg-[#D4AF37]/10 rounded-full blur-[100px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <p className="text-white/25 text-base sm:text-lg md:text-xl font-black uppercase tracking-[0.3em] mb-1">
            Momento
          </p>
          <h2 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase tracking-tight text-[#D4AF37] mb-3 leading-none">
            E.Ú.G.E
          </h2>
          <p className="font-mono text-[10px] sm:text-xs md:text-sm text-white/40 tracking-[0.15em] uppercase">
            Eventos Únicos, Grandes Experiencias
          </p>
        </motion.div>

        <div className="flex items-center justify-center gap-3 mt-6">
          <div className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#D4AF37]/40" />
          <div className="w-1.5 h-1.5 rotate-45 border border-[#D4AF37]/40" />
          <div className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#D4AF37]/40" />
        </div>
      </div>
    </section>
  );
};

// ============================================
// EVENT CARD - Estilo Poster Compacto (3 por fila en móvil)
// ============================================
const EventCardPoster = ({ event, index }) => {
  const eventDate = parseEventDate(event.date);
  const ticketsAvailable = event.ticket_quantity - event.tickets_sold;
  const isSoldOut = ticketsAvailable <= 0;
  const isLowStock = ticketsAvailable < 50 && !isSoldOut;

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/api/s/evento/${event.slug || event.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text: `${event.title} - ${formatEventDate(event.date, "d MMM")}`,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          navigator.clipboard.writeText(shareUrl);
          toast.success('Link copiado');
        }
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copiado');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <Link
        to={`/evento/${event.slug || event.id}`}
        data-testid={`event-card-${event.id}`}
        className="group block"
      >
        <div className="relative overflow-hidden bg-[#0A0A0A]">
          <div className="relative">
            {/* Imagen - aspect ratio más cuadrado para móvil compacto */}
            <div className="relative aspect-[3/4] sm:aspect-[3/4] overflow-hidden">
              <img
                src={event.image_url || 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800'}
                alt={event.title}
                className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
                  event.image_position === 'top' ? 'object-top' : 
                  event.image_position === 'bottom' ? 'object-bottom' : 
                  'object-center'
                }`}
                loading="lazy"
              />
              
              {/* Overlay gradiente */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-90" />
              
              {/* Hover glow */}
              <div className="absolute inset-0 bg-[#D4AF37]/0 group-hover:bg-[#D4AF37]/10 transition-all duration-300" />

              {/* Badge de fecha - Compacto */}
              <div className="absolute top-0 left-0 bg-[#D4AF37] text-black px-1.5 py-1 sm:px-2 sm:py-1.5">
                <div className="text-center">
                  <span className="block text-sm sm:text-lg font-black leading-none">
                    {formatEventDate(event.date, 'd')}
                  </span>
                  <span className="block text-[8px] sm:text-[10px] font-bold uppercase tracking-wider">
                    {formatEventDate(event.date, 'MMM')}
                  </span>
                </div>
              </div>

              {/* Badge de promo 2x1 */}
              {event.active_promo && (
                <div className="absolute top-[38px] sm:top-[50px] left-1">
                  <EventPromoBadge promo={event.active_promo} compact />
                </div>
              )}

              {/* Badge de estado */}
              {isSoldOut && (
                <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-zinc-900/90 border border-zinc-700 px-1 py-0.5 sm:px-2 sm:py-1">
                  <span className="text-zinc-400 text-[8px] sm:text-[10px] font-bold uppercase">Agotado</span>
                </div>
              )}
              {isLowStock && (
                <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-red-600 px-1 py-0.5 sm:px-2 sm:py-1">
                  <span className="text-white text-[8px] sm:text-[10px] font-bold uppercase">Últimas</span>
                </div>
              )}

              {/* Share Button - aparece en hover */}
              <button
                onClick={handleShare}
                className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-black/70 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-[#D4AF37] hover:scale-110 z-10"
                data-testid={`share-btn-${event.id}`}
              >
                <Share2 size={14} className="text-white sm:w-4 sm:h-4" />
              </button>

              {/* Contenido sobre la imagen */}
              <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-3">
                {/* Productora con logo */}
                {(event.producer_name || event.producer_logo) && (
                  <div className="flex items-center gap-1.5 mb-1.5 sm:mb-2">
                    {event.producer_logo ? (
                      <img 
                        src={event.producer_logo} 
                        alt={event.producer_name || 'Productora'} 
                        className="w-4 h-4 sm:w-5 sm:h-5 rounded-full object-cover bg-white/10"
                      />
                    ) : (
                      <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[#D4AF37]/30 flex items-center justify-center">
                        <span className="text-[8px] sm:text-[10px] font-bold text-[#D4AF37]">
                          {event.producer_name?.charAt(0) || 'P'}
                        </span>
                      </div>
                    )}
                    <span className="text-[9px] sm:text-xs text-white/70 font-medium truncate">
                      {event.producer_name || 'Productora'}
                    </span>
                  </div>
                )}

                {/* Línea decorativa */}
                <div className="w-6 sm:w-10 h-0.5 bg-[#D4AF37] mb-1.5 sm:mb-2 group-hover:w-12 sm:group-hover:w-16 transition-all duration-300" />
                
                {/* Título - más pequeño en móvil */}
                <h3 className="text-xs sm:text-base md:text-lg font-black uppercase tracking-tight text-white mb-1 sm:mb-2 leading-tight line-clamp-2 group-hover:text-[#D4AF37] transition-colors">
                  {event.title}
                </h3>

                {/* Info compacta */}
                <div className="space-y-0.5 sm:space-y-1">
                  <div className="flex items-center gap-1 sm:gap-1.5">
                    <Clock size={8} className="text-[#D4AF37] sm:w-3 sm:h-3" />
                    <span className="font-mono text-[8px] sm:text-[10px] text-white/70">
                      {formatEventDate(event.date, "EEE").toUpperCase()} {event.time || formatEventDate(event.date, "HH:mm")}hs
                    </span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-1.5">
                    <MapPin size={8} className="text-[#D4AF37] flex-shrink-0 sm:w-3 sm:h-3" />
                    <span className="font-mono text-[8px] sm:text-[10px] text-white/50 truncate">
                      {event.venue_name || event.location}
                    </span>
                  </div>
                </div>
              </div>

              {/* Borde dorado inferior en hover */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 sm:h-1 bg-[#D4AF37] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// ============================================
// EMPTY STATE
// ============================================
const EmptyState = () => (
  <div className="text-center py-20">
    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#121212] border border-[#27272A] flex items-center justify-center">
      <Sparkles size={32} className="text-[#D4AF37]" />
    </div>
    <h3 className="text-2xl font-black uppercase tracking-tight text-white mb-2">
      Próximamente
    </h3>
    <p className="text-[#71717A] max-w-sm mx-auto mb-6">
      Estamos preparando nuevas experiencias increíbles. ¡Seguinos en redes!
    </p>
    <Link 
      to="/"
      className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-[#F59E0B] transition-colors"
    >
      Volver al Inicio
    </Link>
  </div>
);

// ============================================
// MAIN PAGE
// ============================================
const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const [eventsRes, featuredRes] = await Promise.all([
          eventsApi.getPublic(false),
          eventsApi.getFeaturedEvents().catch(() => ({ data: [] }))
        ]);
        setEvents(eventsRes.data);
        setFeaturedEvents(featuredRes.data || []);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // Use featured events if configured, otherwise fall back to first 3 events
  const heroEvents = featuredEvents.length > 0 
    ? featuredEvents 
    : events.slice(0, Math.min(3, events.length));
  
  // Grid shows ALL upcoming events — featured ones included (they appear in
  // the hero banner AND in the listing, so nothing "disappears" when featured)
  const gridEvents = events;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO 
        title="Eventos"
        description="Descubrí todos nuestros eventos. Fiestas exclusivas, música electrónica y las mejores experiencias."
        url="/eventos"
      />
      <Navbar />
      
      {/* Hero Slider */}
      <section data-testid="events-hero">
        {loading ? (
          <div className="aspect-[3/4] sm:aspect-[16/10] lg:aspect-[21/9] bg-[#121212] animate-pulse" />
        ) : heroEvents.length > 0 ? (
          <HeroSlider events={heroEvents} />
        ) : null}
      </section>

      {/* Slogan */}
      <SloganSection />

      {/* Events Grid */}
      {!loading && gridEvents.length > 0 && (
        <section className="py-10 md:py-16 px-4 sm:px-6 md:px-12 pb-24 sm:pb-10" data-testid="events-grid">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              className="mb-8 md:mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-[0.3em] mb-2 block">
                Agenda
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black uppercase tracking-tighter text-white">
                Próximos Eventos
              </h2>
            </motion.div>

            {/* Grid con cards - 3 columnas en móvil */}
            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
              {gridEvents.map((event, index) => (
                <EventCardPoster key={event.id} event={event} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {!loading && events.length === 0 && (
        <section className="py-20 px-6">
          <EmptyState />
        </section>
      )}

      <Footer />
    </div>
  );
};

export default EventsPage;
